import type { Room } from "@codeshare/session";
import type { TermChannel, TermHost } from "@codeshare/terminal/stream";
import { getIdentityStore } from "../session/identity";
import { getWorkspaceSession } from "../workspace/use-workspace";
import type { RunSession, RunStart } from "./run-session";

/** The stream protocol (with its headless terminal) loads only once there's a room. */
const loadStream = () => import("@codeshare/terminal/stream");

/** What the UI shows about sharing: the session we host (if any). */
export interface SharingState {
  hosting?: { sessionId: string; allowInput: boolean };
  /** Session ids whose host has opened a stream to us. */
  mirrors: string[];
}

interface Hosted {
  sessionId: string;
  stream: TermHost;
  channels: Map<string, { close(): void } & TermChannel>;
  cleanup: (() => void)[];
}

/**
 * Shares this user's runs with the room and receives everyone else's (docs/PRD.md, "Terminal
 * streaming"):
 * - host: each run becomes a run session in the document, streamed to every peer over its own
 *   `term:<session id>` channel (late joiners included); guests may type only if allowed.
 * - guest: keeps the stream channels hosts open to us, for the mirror tabs.
 * Runs themselves always stay local: peers can only watch, or type when the host allows it.
 */
export class TerminalSharing {
  readonly #runs: RunSession;
  readonly #listeners = new Set<() => void>();
  readonly #incoming = new Map<string, TermChannel>();
  #room: Room | undefined;
  #roomCleanup: (() => void)[] = [];
  #hosted: Hosted | undefined;
  /** The latest run (hosting it may wait for the stream module to load). */
  #hostingRun: RunStart | undefined;
  #state: SharingState = { mirrors: [] };

  constructor(runs: RunSession) {
    this.#runs = runs;
    runs.onRun((run) => void this.#host(run));
  }

  get state(): SharingState {
    return this.#state;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Called once a room is joined. */
  attachRoom(room: Room): void {
    this.detachRoom();
    this.#room = room;
    void loadStream();
    this.#roomCleanup.push(
      room.onChannel("term", (_peerId, channel) => {
        const sessionId = channel.label?.slice("term:".length);
        if (!sessionId) return;
        this.#incoming.set(sessionId, channel);
        channel.onClose(() => {
          if (this.#incoming.get(sessionId) === channel) {
            this.#incoming.delete(sessionId);
            this.#update();
          }
        });
        this.#update();
      }),
      room.onPeersChange(() => this.#openStreams()),
    );
    // A program already running when the room starts (Share mid-run) is shared too.
    const running = this.#runs.current;
    if (running) void this.#host(running);
  }

  /** Called after leaving the room. */
  detachRoom(): void {
    for (const fn of this.#roomCleanup.splice(0)) fn();
    this.#stopHosting();
    this.#incoming.clear();
    this.#room = undefined;
    this.#update();
  }

  /** The stream of another person's session, if its host has opened one to us. */
  mirrorChannel(sessionId: string): TermChannel | undefined {
    return this.#incoming.get(sessionId);
  }

  /** Lets guests type into the session we host (or stops them). */
  setAllowInput(allow: boolean): void {
    const hosted = this.#hosted;
    if (!hosted) return;
    hosted.stream.allowInput = allow;
    getWorkspaceSession().runSessions.update(hosted.sessionId, { allowInput: allow });
    this.#update();
  }

  async #host(run: RunStart): Promise<void> {
    this.#stopHosting();
    this.#hostingRun = run;
    const room = this.#room;
    if (!room) return;
    const { TermHost } = await loadStream();
    // A newer run (or leaving the room) while the module loaded supersedes this one.
    if (this.#room !== room || this.#hostingRun !== run) return;
    const workspace = getWorkspaceSession();
    const sessions = workspace.runSessions;
    const fileId = workspace.workspace.findByPath(run.entry)?.id;
    const sessionId = sessions.start({
      hostPeerId: room.selfId,
      hostName: getIdentityStore().get().name,
      file: run.entry,
      runnerId: run.runnerId,
      ...(fileId ? { fileId } : {}),
    });
    const stream = new TermHost({ ...this.#runs.size, onInput: (data) => this.#runs.input(data) });
    const hosted: Hosted = { sessionId, stream, channels: new Map(), cleanup: [] };
    this.#hosted = hosted;
    // What the run printed so far, then everything after (no gap: this all runs synchronously).
    for (const data of this.#runs.output) stream.write(data);
    hosted.cleanup.push(
      this.#runs.onWrite((data) => stream.write(data)),
      this.#runs.onResize(({ cols, rows }) => stream.resize(cols, rows)),
    );
    void run.exit.then((exit) => {
      // After the run's own closing note reaches the terminal (and so the stream).
      setTimeout(() => {
        if (this.#hosted !== hosted) return;
        stream.exit({ code: exit.code, reason: exit.reason });
        sessions.update(sessionId, { status: "exited", exitCode: exit.code, endedAt: Date.now() });
      }, 0);
    });
    this.#openStreams();
    this.#update();
  }

  /** Opens the hosted session's stream to every ready peer that doesn't have it yet. */
  #openStreams(): void {
    const hosted = this.#hosted;
    const room = this.#room;
    if (!hosted || !room) return;
    for (const peer of room.peers()) {
      if (peer.state !== "ready" || !peer.channels.term || hosted.channels.has(peer.id)) continue;
      const channel = room.openChannel(peer.id, `term:${hosted.sessionId}`);
      if (!channel) continue;
      hosted.channels.set(peer.id, channel);
      channel.onClose(() => {
        if (hosted.channels.get(peer.id) === channel) hosted.channels.delete(peer.id);
      });
      hosted.stream.addGuest(channel);
    }
  }

  #stopHosting(): void {
    const hosted = this.#hosted;
    if (!hosted) return;
    this.#hosted = undefined;
    for (const fn of hosted.cleanup) fn();
    hosted.stream.destroy();
    for (const channel of hosted.channels.values()) channel.close();
  }

  #update(): void {
    const hosted = this.#hosted;
    this.#state = {
      ...(hosted
        ? { hosting: { sessionId: hosted.sessionId, allowInput: hosted.stream.allowInput } }
        : {}),
      mirrors: [...this.#incoming.keys()],
    };
    for (const listener of this.#listeners) listener();
  }
}
